import LandingLayout from './LandingLayout';
import { useState, type FormEvent } from 'react';
import { useLandingAnimations } from './useLandingAnimations';
import { useI18n } from '../../i18n/LanguageContext';
import { LandingLocale, landingCopy } from './copy';
import { getSalesContactMessages, sendSalesInquiry } from './salesContact';
import { resolveLandingContext } from './regional';
import { useLocation } from 'react-router-dom';

const featureVideoByLocale: Record<LandingLocale, string> = {
    en: 'https://www.youtube.com/embed/H1RTBu1E0O8?start=2',
    es: 'https://www.youtube.com/embed/NyYYqamN6FM',
    it: 'https://www.youtube.com/embed/H1RTBu1E0O8?start=2',
};

export default function TechnologyPage() {
    useLandingAnimations();
    const location = useLocation();
    const { language } = useI18n();
    const { locale } = resolveLandingContext(location.pathname, language);
    const copy = landingCopy[locale].aiCore;
    const messages = getSalesContactMessages(locale);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formMessage, setFormMessage] = useState('');
    const featureVideoUrl = featureVideoByLocale[locale];
    const handleTechnicalBriefing = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmitting) {
            return;
        }

        const form = event.currentTarget;
        const formData = new FormData(form);
        const email = String(formData.get('email') ?? '');

        setFormMessage('');
        setIsSubmitting(true);
        try {
            await sendSalesInquiry({
                requestType: 'REQUEST_TECHNICAL_BRIEFING',
                locale,
                email,
                sourcePage: 'features',
            });
            setFormMessage(messages.success);
            form.reset();
        } catch {
            setFormMessage(messages.error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <LandingLayout pageKey="technology">
            <section className="tech-header">
                <div className="container">
                    <h1 className="reveal-text">{copy.headerTitle}</h1>
                    <p className="tech-features-intro">{copy.featuresIntro}</p>
                </div>
            </section>
            <section className="deep-dive tech-video-section">
                <div className="container tech-video-stack">
                    <p className="tech-video-subtitle">{copy.videoSubtitle}</p>
                    <div className="tech-video-shell">
                        <iframe
                            src={featureVideoUrl}
                            title={`${copy.headerTitle} video`}
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                        />
                    </div>
                </div>
            </section>
            {copy.features.map((feature, index) => (
                <section key={feature.image} className="deep-dive tech-feature-section">
                    <div className={`container deep-dive-grid ${index % 2 === 1 ? 'tech-image-left' : 'tech-image-right'}`}>
                        <h2 className="tech-feature-title">{feature.title}</h2>
                        <div className="dd-image tech-feature-image-shell">
                            <img src={feature.image} alt={feature.imageAlt} loading="lazy" />
                        </div>
                        <div className="dd-content">
                            <p>{feature.body}</p>
                            <p className="tech-feature-detail">{feature.detail}</p>
                            <ul className="tech-feature-highlights">
                                {feature.highlights.map((highlight) => (
                                    <li key={`${feature.title}-${highlight.title}`}>
                                        <div className="tech-feature-highlight-title">
                                            <i className="ph ph-check"></i>  
                                            {highlight.title}
                                        </div>
                                        <p>{highlight.body}</p>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            ))}

            <section id="contact" className="contact-section">
                <div className="container">
                    <h2>{copy.contactTitle}</h2>
                    <p>{copy.contactBody}</p>
                    <form className="contact-form" onSubmit={handleTechnicalBriefing}>
                        <input type="email" name="email" placeholder={copy.contactEmailPlaceholder} required />
                        <button type="submit" disabled={isSubmitting}>{isSubmitting ? messages.sending : copy.contactSubmit}</button>
                    </form>
                    {formMessage ? <p className="contact-form-feedback" role="status" aria-live="polite">{formMessage}</p> : null}
                </div>
            </section>
        </LandingLayout>
    );
}
